import React, { useRef } from 'react';
import { Button } from '../ui/button';
import {
  Save, ZoomIn, ZoomOut, Undo, Redo, Eraser, Square, Download,
  Circle, Columns, Triangle, Hexagon, Image, X, Layers, Sliders, Spline, Maximize2, Edit2, Check, PenTool, ArrowUpDown, Paintbrush
} from 'lucide-react';
import { CZ_COLORS } from './CeilingZoneModal';

const SHAPE_TOOLS = [
  { mode: 'freeform', label: 'Serbest Form', icon: PenTool,
    hint: 'Her tıklama nokta ekler · İlk noktaya tık veya çift tık ile kapat · İstediğin şekilde polygon stand çiz' },
  { mode: 'triangle', label: 'Üçgen', icon: Triangle,
    hint: 'Her tıklama nokta ekler · Çift tık veya ilk noktaya tık ile kapat' },
  { mode: 'circle',   label: 'Daire',  icon: Circle,
    hint: 'Sürükleyerek daire / elips çiz' },
];

export const COLUMN_TYPES = [
  { type: 'square_column',  label: 'Kare' },
  { type: 'rect_column',   label: 'Dikdörtgen' },
  { type: 'circle_column', label: 'Silindir' },
];

/**
 * CanvasToolbar – draw mode controls + background + boundary tools
 */
export function CanvasToolbar({
  drawMode, setDrawMode,
  gridEditor,
  zoom, setZoom,
  onSave, onExportPNG,
  // column
  onOpenColumnConfig,
  // background image
  backgroundImageUrl, backgroundOpacity, setBackgroundOpacity,
  onUploadBackground, onRemoveBackground, bgUploading,
  // boundary
  boundaryPath, onClearBoundary, onFitToBoundary,
  onSaveBoundaryEdit, onCancelBoundaryEdit,
  boundaryPreviewPath,
  // ceiling zones
  ceilingZones,
}) {
  const fileInputRef = useRef(null);
  const activeDraw = ['grid', 'freeform', 'triangle', 'circle', 'column', 'boundary', 'bezier', 'editBoundary', 'ceilingZoneRect', 'ceilingZoneBrush'].includes(drawMode);

  return (
    <div className="space-y-2">
      {/* Row 1: Draw modes */}
      <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-lg">
        {/* Grid stand */}
        <Button size="sm" variant={drawMode === 'grid' ? 'default' : 'outline'}
          onClick={() => setDrawMode('grid')} className="gap-1.5 text-xs" data-testid="mode-grid-form">
          <Square className="w-3.5 h-3.5" /> Grid Form
        </Button>

        {/* Shape tools */}
        {SHAPE_TOOLS.map(t => (
          <Button key={t.mode} size="sm"
            variant={drawMode === t.mode ? 'default' : 'outline'}
            onClick={() => setDrawMode(t.mode)}
            className="gap-1.5 text-xs" title={t.hint}
            data-testid={`mode-${t.mode}`}>
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </Button>
        ))}

        {/* Column */}
        <div className="flex gap-1 items-center border-l pl-2 ml-1">
          <Button size="sm"
            variant={drawMode === 'column' ? 'default' : 'outline'}
            onClick={onOpenColumnConfig}
            className="gap-1.5 text-xs"
            title="Kolon yapılandır ve yerleştir"
            data-testid="mode-column">
            <Columns className="w-3.5 h-3.5" /> Kolon
            {drawMode === 'column' && (
              <span className="ml-1 w-2 h-2 rounded-full bg-green-400 inline-block" />
            )}
          </Button>
        </div>

        {/* Hall Boundary */}
        <div className="flex gap-1 items-center border-l pl-2 ml-1 flex-wrap">
          <Button size="sm" variant={drawMode === 'boundary' ? 'default' : 'outline'}
            onClick={() => setDrawMode('boundary')} className="gap-1.5 text-xs"
            title="Hall sınırını düz çizgilerle çiz"
            data-testid="mode-boundary">
            <Hexagon className="w-3.5 h-3.5" /> Hall Sınırı
          </Button>
          <Button size="sm" variant={drawMode === 'bezier' ? 'default' : 'outline'}
            onClick={() => setDrawMode('bezier')} className="gap-1.5 text-xs"
            title="Hall sınırını eğrisel (Bézier) çizgilerle çiz — yuvarlak köşeler için ideal"
            data-testid="mode-bezier">
            <Spline className="w-3.5 h-3.5" /> Eğri Sınır
          </Button>
          {/* Sınır Düzenle — her zaman görünür, sınır yoksa dikdörtgen oluşturur */}
          <Button size="sm"
            variant={drawMode === 'editBoundary' ? 'default' : 'outline'}
            onClick={() => setDrawMode(drawMode === 'editBoundary' ? 'grid' : 'editBoundary')}
            className={`gap-1.5 text-xs ${drawMode === 'editBoundary' ? 'bg-orange-500 hover:bg-orange-600 border-orange-500' : 'text-orange-600 hover:text-orange-800 hover:border-orange-400'}`}
            title="Sınırı sürükleyerek düzenle — sınır yoksa hol alanını başlangıç olarak kullanır"
            data-testid="mode-edit-boundary">
            <Edit2 className="w-3.5 h-3.5" /> Sınır Düzenle
          </Button>
          {boundaryPath && drawMode !== 'editBoundary' && (
            <>
              <Button size="sm" variant="outline"
                onClick={onClearBoundary} className="text-xs h-8 px-2 text-red-500 hover:text-red-700"
                title="Sınırı kaldır" data-testid="clear-boundary-btn">
                <X className="w-3 h-3 mr-0.5" /> Sil
              </Button>
              <Button size="sm" variant="outline"
                onClick={onFitToBoundary} className="text-xs h-8 px-2 text-blue-600 hover:text-blue-800"
                title="Görünümü sınıra odakla" data-testid="fit-boundary-btn">
                <Maximize2 className="w-3 h-3 mr-0.5" /> Odakla
              </Button>
              <span className="text-xs text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded border border-blue-200">
                Sınır aktif
              </span>
            </>
          )}
          {/* editBoundary mode controls */}
          {drawMode === 'editBoundary' && (
            <>
              <Button size="sm" onClick={onSaveBoundaryEdit}
                className="text-xs h-8 px-3 bg-green-600 hover:bg-green-700 text-white border-green-600 gap-1"
                data-testid="save-boundary-edit-btn">
                <Check className="w-3.5 h-3.5" /> Kaydet
              </Button>
              <Button size="sm" variant="outline"
                onClick={onCancelBoundaryEdit}
                className="text-xs h-8 px-2 text-gray-600"
                data-testid="cancel-boundary-edit-btn">
                <X className="w-3 h-3 mr-0.5" /> İptal
              </Button>
            </>
          )}
        </div>

        {/* Grid/surface tools */}
        <div className="flex gap-1 items-center border-l pl-2 ml-1 flex-wrap">
          {Object.entries(gridEditor.TOOL_TYPES).map(([key, value]) => (
            <Button key={value} onClick={() => gridEditor.setSelectedTool(value)}
              variant={gridEditor.selectedTool === value ? 'default' : 'outline'}
              size="sm" className="gap-1.5 text-xs" data-testid={`tool-${value}`}>
              <div className="w-3.5 h-3.5 rounded" style={{ backgroundColor: gridEditor.TOOL_COLORS[value] }} />
              {gridEditor.TOOL_LABELS[value]}
            </Button>
          ))}
        </div>

        {/* Ceiling Zone tools */}
        <div className="flex gap-1 items-center border-l pl-2 ml-1 flex-wrap">
          <Button size="sm"
            variant={drawMode === 'ceilingZoneRect' ? 'default' : 'outline'}
            onClick={() => setDrawMode(drawMode === 'ceilingZoneRect' ? 'grid' : 'ceilingZoneRect')}
            className={`gap-1.5 text-xs ${drawMode === 'ceilingZoneRect' ? 'bg-amber-500 hover:bg-amber-600 border-amber-500 text-white' : 'text-amber-700 hover:border-amber-400 border-amber-300'}`}
            title="Dikdörtgen alan sürükleyerek tavan yüksekliği bölgesi ekle"
            data-testid="mode-ceiling-zone-rect">
            <ArrowUpDown className="w-3.5 h-3.5" /> Tavan Bölgesi
          </Button>
          <Button size="sm"
            variant={drawMode === 'ceilingZoneBrush' ? 'default' : 'outline'}
            onClick={() => setDrawMode(drawMode === 'ceilingZoneBrush' ? 'grid' : 'ceilingZoneBrush')}
            className={`gap-1.5 text-xs ${drawMode === 'ceilingZoneBrush' ? 'bg-amber-500 hover:bg-amber-600 border-amber-500 text-white' : 'text-amber-700 hover:border-amber-400 border-amber-300'}`}
            title="Fırça ile hücre hücre tavan bölgesi boyama"
            data-testid="mode-ceiling-zone-brush">
            <Paintbrush className="w-3.5 h-3.5" /> Fırça
          </Button>
          {ceilingZones?.length > 0 && (
            <div className="flex gap-1 items-center ml-1 flex-wrap">
              {ceilingZones.map(z => {
                const c = CZ_COLORS[z.color_index ?? 0];
                return (
                  <span key={z.id}
                    className="text-xs px-1.5 py-0.5 rounded border font-medium"
                    style={{ backgroundColor: c.hex, borderColor: c.border, color: c.text }}>
                    ↕{z.ceiling_height}m / ▲{z.max_stand_height}m
                  </span>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Row 2: Background image panel */}
      <div className="flex flex-wrap items-center gap-3 px-3 py-2 bg-slate-50 rounded-lg border border-slate-200">
        <div className="flex items-center gap-1.5 text-xs font-medium text-gray-600">
          <Layers className="w-3.5 h-3.5" /> Zemin Planı
        </div>

        {!backgroundImageUrl ? (
          <>
            <Button size="sm" variant="outline" className="gap-1.5 text-xs h-8"
              onClick={() => fileInputRef.current?.click()}
              disabled={bgUploading}
              data-testid="upload-bg-btn">
              <Image className="w-3.5 h-3.5" />
              {bgUploading ? 'Yükleniyor…' : 'PNG/JPG Yükle'}
            </Button>
            <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/webp"
              className="hidden" onChange={e => { if (e.target.files?.[0]) onUploadBackground(e.target.files[0]); }} />
            <span className="text-xs text-gray-400">Mevcut zemin planınızı arka plan olarak yükleyin</span>
          </>
        ) : (
          <>
            <div className="flex items-center gap-2">
              <Sliders className="w-3.5 h-3.5 text-gray-400" />
              <span className="text-xs text-gray-500">Opaklık</span>
              <input type="range" min="5" max="80" step="5"
                value={Math.round(backgroundOpacity * 100)}
                onChange={e => setBackgroundOpacity(Number(e.target.value) / 100)}
                className="w-24 h-1.5 accent-teal-600"
                data-testid="bg-opacity-slider" />
              <span className="text-xs text-gray-600 w-8 text-right">
                {Math.round(backgroundOpacity * 100)}%
              </span>
            </div>
            <Button size="sm" variant="outline"
              className="gap-1 text-xs h-8 text-red-500 hover:text-red-700"
              onClick={onRemoveBackground} data-testid="remove-bg-btn">
              <X className="w-3 h-3" /> Kaldır
            </Button>
            <span className="text-xs text-teal-600 bg-teal-50 px-1.5 py-0.5 rounded border border-teal-200">
              Arka plan aktif
            </span>
          </>
        )}
      </div>

      {/* Row 3: Actions + zoom + hints */}
      <div className="flex gap-2 flex-wrap items-center">
        <Button onClick={gridEditor.handleUndo} size="sm" disabled={!gridEditor.canUndo} data-testid="undo-btn">
          <Undo className="w-4 h-4 mr-1" /> Geri Al
        </Button>
        <Button onClick={gridEditor.handleRedo} size="sm" disabled={!gridEditor.canRedo} data-testid="redo-btn">
          <Redo className="w-4 h-4 mr-1" /> İleri Al
        </Button>
        <Button onClick={gridEditor.handleClearAll} size="sm" variant="outline" data-testid="clear-all-btn">
          <Eraser className="w-4 h-4 mr-1" /> Temizle
        </Button>

        {/* Context hints */}
        {drawMode === 'triangle' && (
          <span className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded border border-blue-200">
            Her tıklama nokta ekler · Çift tık veya ilk noktaya tıkla
          </span>
        )}
        {drawMode === 'boundary' && (
          <span className="text-xs text-indigo-600 bg-indigo-50 px-2 py-1 rounded border border-indigo-200">
            Hall sınırı çiziliyor · {boundaryPreviewPath?.length || 0} nokta · Çift tık veya ilk noktaya tıkla
          </span>
        )}
        {drawMode === 'bezier' && (
          <span className="text-xs text-purple-600 bg-purple-50 px-2 py-1 rounded border border-purple-200">
            Eğrisel sınır · {boundaryPreviewPath?.length || 0} nokta · Tıkla nokta ekle · Çift tık veya ilk noktaya tıkla
          </span>
        )}
        {drawMode === 'editBoundary' && (
          <span className="text-xs text-orange-600 bg-orange-50 px-2 py-1 rounded border border-orange-200">
            Noktaları sürükle · Kaydet veya Esc ile iptal
          </span>
        )}        {drawMode === 'circle' && (
          <span className="text-xs text-purple-600 bg-purple-50 px-2 py-1 rounded border border-purple-200">
            Sürükleyerek daire/elips çiz
          </span>
        )}
        {drawMode === 'column' && (
          <span className="text-xs text-orange-600 bg-orange-50 px-2 py-1 rounded border border-orange-200">
            Canvas'ta basılı tut, sürükle ve bırak → kolon yerleşir
          </span>
        )}
        {(drawMode === 'ceilingZoneRect' || drawMode === 'ceilingZoneBrush') && (
          <span className="text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded border border-amber-200">
            {drawMode === 'ceilingZoneRect'
              ? 'Dikdörtgen sürükle → tavan yüksekliği gir · Mevcut bölgeye tıkla → düzenle/sil'
              : 'Tıklayarak/sürükleyerek boyama yap → fareyi bırak → yükseklik gir · Mevcut bölgeye tıkla → düzenle/sil'}
          </span>
        )}

        <div className="flex-1" />
        <Button onClick={() => setZoom(prev => Math.min(prev + 0.2, 3))} size="sm" data-testid="zoom-in-btn">
          <ZoomIn className="w-4 h-4" />
        </Button>
        <Button onClick={() => setZoom(prev => Math.max(prev - 0.2, 0.3))} size="sm" data-testid="zoom-out-btn">
          <ZoomOut className="w-4 h-4" />
        </Button>
        <span className="text-sm py-1.5 px-3 bg-gray-100 rounded">
          Zoom: {(zoom * 100).toFixed(0)}%
        </span>
        <Button onClick={onSave} data-testid="save-layout-btn">
          <Save className="w-4 h-4 mr-2" /> Kaydet
        </Button>
        {onExportPNG && (
          <Button onClick={onExportPNG} variant="outline" size="sm" data-testid="export-png-btn">
            <Download className="w-4 h-4 mr-1" /> PNG
          </Button>
        )}
      </div>
    </div>
  );
}
